import React, { useMemo } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Controls,
  Background,
  BackgroundVariant,
  MiniMap,
  NodeProps,
  Handle,
  Position,
  useNodesState,
  useEdgesState
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Database,
  Server,
  Shield,
  Zap,
  Archive,
  RefreshCw
} from 'lucide-react';
import { DataFlowMap } from '../../services/processOptimizationAPI';

interface VisualDataFlowDiagramProps {
  dataFlow: DataFlowMap;
}

interface SourceNodeData {
  label: string;
  description: string;
}

interface TransformNodeData {
  label: string;
  system: string;
  description: string;
  step: number;
}

interface StorageNodeData {
  label: string;
  type: string;
  retention: string;
}

interface AccessControlNodeData {
  label: string;
  roles: string;
  permissions: string;
}

// Custom node for data source
const SourceNode = ({ data }: { data: SourceNodeData }) => (
  <div
    className="bg-green-50 border-2 border-green-400 rounded-lg p-2 shadow-lg min-w-[180px] max-w-[220px] hover:shadow-xl transition-shadow"
    title="Data at Rest - Source location where data originates"
  >
    <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-green-600" />
    <div className="flex items-start gap-2">
      <div className="bg-green-100 p-1.5 rounded-lg">
        <Database className="w-5 h-5 text-green-700" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-bold text-sm text-green-900">Data Source</h4>
          <span className="px-2 py-0.5 bg-green-600 text-white text-[10px] font-bold rounded uppercase">At Rest</span>
        </div>
        <p className="text-xs font-semibold text-gray-900">{data.label}</p>
        <p className="text-xs text-gray-600 mt-1">{data.description}</p>
      </div>
    </div>
  </div>
);

// Custom node for transformations
const TransformNode = ({ data }: { data: TransformNodeData }) => (
  <div
    className="bg-blue-50 border-2 border-blue-400 rounded-lg p-2 shadow-lg min-w-[180px] max-w-[220px] hover:shadow-xl transition-shadow"
    title="Data in Motion - Transformation and processing step"
  >
    <Handle type="target" position={Position.Top} className="w-3 h-3 bg-blue-600" />
    <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-blue-600" />
    <div className="flex items-start gap-2">
      <div className="bg-blue-100 p-1.5 rounded-lg flex-shrink-0">
        <RefreshCw className="w-5 h-5 text-blue-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
            {data.step}
          </div>
          <h4 className="font-bold text-sm text-blue-900 truncate">{data.label}</h4>
          <span className="px-2 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded uppercase whitespace-nowrap">In Motion</span>
        </div>
        <p className="text-xs text-gray-500 mb-1">System: {data.system}</p>
        <p className="text-xs text-gray-600">{data.description}</p>
      </div>
    </div>
  </div>
);

// Custom node for storage
const StorageNode = ({ data }: { data: StorageNodeData }) => (
  <div
    className="bg-green-50 border-2 border-green-400 rounded-lg p-2 shadow-lg min-w-[180px] max-w-[220px] hover:shadow-xl transition-shadow"
    title={`Data at Rest - ${data.type === 'permanent' ? 'Permanent' : 'Temporary'} storage with retention policy`}
  >
    <Handle type="target" position={Position.Top} className="w-3 h-3 bg-green-600" />
    <div className="flex items-start gap-2">
      <div className="bg-green-100 p-1.5 rounded-lg">
        <Archive className="w-5 h-5 text-green-700" />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h4 className="font-bold text-sm text-green-900">Data Storage</h4>
          <div className="flex gap-1">
            <span className="px-2 py-0.5 bg-green-600 text-white text-[10px] font-bold rounded uppercase">At Rest</span>
            <span className={`${data.type === 'permanent' ? 'bg-emerald-600' : 'bg-amber-600'} text-white text-[10px] px-2 py-0.5 rounded font-bold uppercase`}>
              {data.type}
            </span>
          </div>
        </div>
        <p className="text-xs font-semibold text-gray-900">{data.label}</p>
        <div className="flex items-center gap-1 mt-1">
          <span className="text-xs text-gray-600">🗓️ Retention:</span>
          <span className="text-xs font-semibold text-gray-900">{data.retention}</span>
        </div>
      </div>
    </div>
  </div>
);

// Custom node for access control
const AccessControlNode = ({ data }: { data: AccessControlNodeData }) => (
  <div
    className="bg-red-50 border-2 border-red-400 rounded-lg p-2 shadow-lg min-w-[180px] max-w-[220px] hover:shadow-xl transition-shadow"
    title="Access Control Point - Security gate controlling who can access data"
  >
    <Handle type="target" position={Position.Top} className="w-3 h-3 bg-red-600" />
    <Handle type="source" position={Position.Bottom} className="w-3 h-3 bg-red-600" />
    <div className="flex items-start gap-2">
      <div className="bg-red-100 p-1.5 rounded-lg">
        <Shield className="w-5 h-5 text-red-700" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-bold text-sm text-red-900">Access Control</h4>
          <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded uppercase">Security</span>
        </div>
        <p className="text-xs font-semibold text-gray-900">{data.label}</p>
        <p className="text-xs text-gray-600 mt-1">
          <span className="font-medium">👥 Roles:</span> {data.roles}
        </p>
        <p className="text-xs text-gray-600">
          <span className="font-medium">🔐 Permissions:</span> {data.permissions}
        </p>
      </div>
    </div>
  </div>
);

const nodeTypes = {
  source: SourceNode,
  transform: TransformNode,
  storage: StorageNode,
  accessControl: AccessControlNode
};

const VisualDataFlowDiagram: React.FC<VisualDataFlowDiagramProps> = ({ dataFlow }) => {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    let yPosition = 80;
    const xPosition = 400; // More centered
    const verticalSpacing = 280; // Increased spacing to prevent overlap and improve arrow clarity

    // 1. Source Node
    nodes.push({
      id: 'source',
      type: 'source',
      position: { x: xPosition, y: yPosition },
      draggable: true,
      data: {
        label: dataFlow.source.system,
        description: dataFlow.source.description
      }
    });

    yPosition += verticalSpacing;

    // 2. Transformation Nodes
    dataFlow.transformations?.forEach((transform, idx) => {
      const nodeId = `transform_${idx}`;
      nodes.push({
        id: nodeId,
        type: 'transform',
        position: { x: xPosition, y: yPosition },
        draggable: true,
        data: {
          label: transform.step,
          system: transform.system,
          description: transform.description,
          step: idx + 1
        }
      });

      // Connect to previous node
      const previousNode = idx === 0 ? 'source' : `transform_${idx - 1}`;
      edges.push({
        id: `edge_${previousNode}_${nodeId}`,
        source: previousNode,
        target: nodeId,
        label: 'Transform',
        animated: true,
        style: { stroke: '#2563eb', strokeWidth: 3 }, // Blue for data in motion
        labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
        labelStyle: { fontSize: 11, fontWeight: 600, fill: '#2563eb' },
        type: 'smoothstep',
        markerEnd: { type: 'arrowclosed', color: '#2563eb' }
      });

      yPosition += verticalSpacing;
    });

    // 3. Access Control Nodes (positioned to the right of transformations)
    dataFlow.accessControl?.forEach((ac, idx) => {
      const nodeId = `access_${idx}`;
      const insertAfterTransform = Math.floor((dataFlow.transformations?.length || 0) / (dataFlow.accessControl?.length || 1)) * (idx + 1) - 1;
      const yPos = 80 + verticalSpacing * (insertAfterTransform + 1.5);

      nodes.push({
        id: nodeId,
        type: 'accessControl',
        position: { x: xPosition + 350, y: yPos },
        draggable: true,
        data: {
          label: ac.point,
          roles: ac.roles.join(', '),
          permissions: ac.permissions
        }
      });

      // Connect to corresponding transformation
      const connectTo = insertAfterTransform >= 0 ? `transform_${insertAfterTransform}` : 'source';
      edges.push({
        id: `edge_${connectTo}_${nodeId}`,
        source: connectTo,
        target: nodeId,
        label: 'Security Check',
        style: { stroke: '#dc2626', strokeWidth: 2, strokeDasharray: '5,5' },
        labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
        labelStyle: { fontSize: 11, fontWeight: 600, fill: '#dc2626' },
        type: 'smoothstep',
        markerEnd: { type: 'arrowclosed', color: '#dc2626' }
      });
    });

    // 4. Storage Nodes
    const lastTransformIdx = (dataFlow.transformations?.length || 1) - 1;
    const lastTransformNode = lastTransformIdx >= 0 ? `transform_${lastTransformIdx}` : 'source';

    dataFlow.storage?.forEach((store, idx) => {
      const nodeId = `storage_${idx}`;
      const storageSpacing = 320;
      const storageStartX = xPosition - (dataFlow.storage!.length - 1) * storageSpacing / 2;

      nodes.push({
        id: nodeId,
        type: 'storage',
        position: { x: storageStartX + (idx * storageSpacing), y: yPosition },
        draggable: true,
        data: {
          label: store.system,
          type: store.type,
          retention: store.retention
        }
      });

      // Connect from last transformation
      edges.push({
        id: `edge_${lastTransformNode}_${nodeId}`,
        source: lastTransformNode,
        target: nodeId,
        label: store.type === 'permanent' ? 'Store (Permanent)' : 'Store (Temporary)',
        style: {
          stroke: '#16a34a', // Green for data at rest
          strokeWidth: 3
        },
        labelBgStyle: { fill: '#ffffff', fillOpacity: 0.9 },
        labelStyle: {
          fontSize: 11,
          fontWeight: 600,
          fill: '#16a34a'
        },
        type: 'smoothstep',
        markerEnd: {
          type: 'arrowclosed',
          color: '#16a34a'
        }
      });
    });

    return { nodes, edges };
  }, [dataFlow]);

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  return (
    <div className="h-[700px] w-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-gray-200">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-left"
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls />
        <MiniMap
          nodeStrokeWidth={3}
          zoomable
          pannable
          className="bg-white border border-gray-300 rounded"
        />
      </ReactFlow>

      {/* Legend */}
      <div className="absolute bottom-4 right-4 bg-white border-2 border-gray-300 rounded-lg p-3 shadow-lg">
        <h5 className="text-xs font-bold text-gray-900 mb-2">Data Flow Legend</h5>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-600 rounded"></div>
            <span className="text-xs text-gray-700">Data at Rest</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-600 rounded"></div>
            <span className="text-xs text-gray-700">Data in Motion</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-600 rounded"></div>
            <span className="text-xs text-gray-700">Access Control</span>
          </div>
          <div className="border-t border-gray-200 my-1 pt-1.5">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-600 rounded"></div>
              <span className="text-xs text-gray-700">Permanent Storage</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-4 h-4 bg-amber-600 rounded"></div>
              <span className="text-xs text-gray-700">Temporary Storage</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualDataFlowDiagram;
